// The app's sidebar: the logo, every group of pages with its label, and help
// at the bottom. The signed-in user is on the top bar (NavBar).
//
// Redesigned 2026-10-01. Groups are always open, under small plain labels —
// they used to be collapsible sections with only the current one open, so
// moving from Directory to Billing took two clicks, and a section's labels were
// styled through Typography props MUI v9 ignores (they meant small caps and
// rendered as plain grey words). Rows are full width, the current page tinted
// with a bar on the right; the icon tiles and grey labels are the earlier look,
// kept (sidebar/navRow.jsx). Billing and Payments are dropdowns
// (SidebarNavGroup). Which items a user sees is sidebar/sidebarSections.jsx.
import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Divider, Drawer, IconButton, List, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAuth } from '@app/providers/AuthProvider';
import { useUnresolvedErrorsCount } from '@features/admin/api/errorLogs';
import useResponsive from '@shared/hooks/useResponsive';
import SidebarFooter from './SidebarFooter';
import SidebarNavItem from './sidebar/SidebarNavItem';
import SidebarNavGroup from './sidebar/SidebarNavGroup';
import SidebarBrand from './sidebar/SidebarBrand';
import { TOP_BAR_HEIGHT, frameLine } from './frame';
import { buildSidebarSections, isItemActive } from './sidebar/sidebarSections';
import { holdsPermission } from '@shared/utils/holdsPermission';
import { PERM } from '@shared/constants/permissions';

const EXPANDED_WIDTH = 248;
const COLLAPSED_WIDTH = 72;

const Aside = ({ mobileOpen, handleDrawerToggle, expanded, onToggleExpand }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  // On mobile, "expanded" is implied by `mobileOpen` (drawer is full-width when open).
  // On desktop, the user explicitly controls expand/collapse via the toggle button.
  const isExpanded = isMobile ? mobileOpen : expanded;
  const drawerWidth = isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${drawerWidth}px`);
  }, [drawerWidth]);

  // Unresolved error count for the Error Logs badge. Fetched only with the
  // permission — otherwise the endpoint 403s and toasts an error.
  const { data: unresolvedErrorCount = 0 } = useUnresolvedErrorsCount({
    enabled: holdsPermission(user, PERM.MANAGE_INTEGRATIONS),
  });

  const sections = useMemo(
    () => buildSidebarSections({ user, unresolvedErrorCount }),
    [user, unresolvedErrorCount],
  );

  // With every group open the list can outgrow the screen; keep the current
  // page in view after navigating.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [location.pathname]);

  const handleNavigate = (item) => {
    if (item.disabled || !item.path) return;
    navigate(item.path);
    if (isMobile) handleDrawerToggle();
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.9)
            : alpha(theme.palette.background.paper, 0.96),
          backgroundImage: 'none',
          boxShadow: 'none',
          borderRight: frameLine(theme),
          position: 'relative',
          height: '100%',
          transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          // Lets the expand/collapse chevron overhang the right edge.
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* The top bar's height and hairline, so the two lines meet as one. */}
      <Box
        data-testid="sidebar-header"
        sx={{ position: 'relative', display: 'flex', alignItems: 'center', height: TOP_BAR_HEIGHT, px: isExpanded ? 1.5 : 1, flexShrink: 0, borderBottom: frameLine(theme) }}
      >
        <SidebarBrand expanded={isExpanded} />

        {!isMobile && onToggleExpand && (
          <Tooltip title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
            <IconButton
              onClick={onToggleExpand}
              size="small"
              aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              sx={{
                position: 'absolute',
                top: '50%',
                right: -14,
                transform: 'translateY(-50%)',
                width: 28,
                height: 28,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                color: 'text.secondary',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                zIndex: 2,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              {isExpanded ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List
        ref={navRef}
        component="nav"
        aria-label="Main"
        disablePadding
        sx={{
          flexGrow: 1,
          pt: 1,
          pb: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // A thin scrollbar that shows only on hover, in a gutter that is
          // always reserved so labels do not shift when it appears.
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 6, backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 4 },
          '&:hover': {
            scrollbarColor: `${alpha(theme.palette.divider, 0.7)} transparent`,
            '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.divider, 0.7) },
          },
        }}
      >
        {sections.map((section, index) => (
          <Box key={section.key} component="li" sx={{ listStyle: 'none' }} aria-labelledby={isExpanded ? `nav-group-${section.key}` : undefined}>
            {isExpanded ? (
              <Typography
                id={`nav-group-${section.key}`}
                variant="caption"
                component="div"
                sx={{ px: 2.5, pt: index === 0 ? 0.5 : 2, pb: 0.5, color: 'text.secondary', fontWeight: 500 }}
              >
                {section.label}
              </Typography>
            ) : (
              index > 0 && <Divider sx={{ mx: 2, my: 1 }} />
            )}
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {section.items.map((item) => (
                <Box component="li" key={item.key}>
                  {item.children ? (
                    <SidebarNavGroup item={item} pathname={location.pathname} expanded={isExpanded} onNavigate={handleNavigate} />
                  ) : (
                    <SidebarNavItem
                      item={item}
                      active={isItemActive(item, location.pathname)}
                      expanded={isExpanded}
                      onNavigate={handleNavigate}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </List>

      <SidebarFooter isExpanded={isExpanded} />
    </Drawer>
  );
};

export default Aside;
